<template>
    <main class="why-page">

        <!-- =====================================================
             HEADER
        ====================================================== -->

        <HeaderSection />


        <!-- =====================================================
             HERO
        ====================================================== -->

        <section class="why-hero">

            <div class="hero-grid"></div>
            <div class="hero-glow hero-glow-one"></div>
            <div class="hero-glow hero-glow-two"></div>

            <div class="why-container">

                <div class="hero-content">

                    <div class="eyebrow">
                        <span class="eyebrow-line"></span>
                        WHY UNITECORE
                    </div>

                    <h1>
                        Technology that
                        <span>works together.</span>
                    </h1>

                    <p class="hero-description">
                        We bring technology, data, security and people together
                        to create smarter, more secure and scalable business
                        environments.
                    </p>

                    <div class="hero-meta">

                        <div class="hero-meta-item">
                            <strong>10+</strong>
                            <span>Years of Experience</span>
                        </div>

                        <div class="hero-meta-item">
                            <strong>750+</strong>
                            <span>Clients Served</span>
                        </div>

                        <div class="hero-meta-item">
                            <strong>75+</strong>
                            <span>Technology Experts</span>
                        </div>

                    </div>

                </div>


                <!-- =================================================
                     HERO VISUAL
                ================================================== -->

                <div class="hero-visual">

                    <div class="orbit orbit-one"></div>
                    <div class="orbit orbit-two"></div>
                    <div class="orbit orbit-three"></div>

                    <div class="core-circle">

                        <div class="core-inner">
                            <span>UC</span>
                        </div>

                    </div>

                    <div class="floating-card card-one">
                        <div class="card-icon">
                            <i class="bi bi-shield-check"></i>
                        </div>

                        <div>
                            <strong>Secure</strong>
                            <span>By Design</span>
                        </div>
                    </div>

                    <div class="floating-card card-two">
                        <div class="card-icon">
                            <i class="bi bi-cloud-check"></i>
                        </div>

                        <div>
                            <strong>Scalable</strong>
                            <span>Infrastructure</span>
                        </div>
                    </div>

                    <div class="floating-card card-three">
                        <div class="card-icon">
                            <i class="bi bi-database-check"></i>
                        </div>

                        <div>
                            <strong>Available</strong>
                            <span>Data</span>
                        </div>
                    </div>

                    <div class="floating-card card-four">
                        <div class="card-icon">
                            <i class="bi bi-bar-chart-line"></i>
                        </div>

                        <div>
                            <strong>Insightful</strong>
                            <span>Analytics</span>
                        </div>
                    </div>

                </div>

            </div>

        </section>


        <!-- =====================================================
             APPROACH
        ====================================================== -->

        <section class="approach-section" id="approach">

            <div class="why-container">

                <div class="section-heading">

                    <div class="section-label">
                        OUR APPROACH
                    </div>

                    <h2>
                        Technology should
                        <span>simplify business.</span>
                    </h2>

                    <p>
                        At UniteCore, we believe technology should work as one
                        connected ecosystem rather than a collection of
                        disconnected solutions.
                    </p>

                </div>


                <div class="approach-grid">

                    <div class="approach-main">

                        <div class="approach-number">
                            01
                        </div>

                        <h3>
                            Understand first.
                            <span>Build better.</span>
                        </h3>

                        <p>
                            We begin by understanding your business,
                            infrastructure, data and operational challenges.
                            This allows us to design solutions around your
                            actual requirements.
                        </p>

                        <div class="approach-line"></div>

                    </div>


                    <div class="approach-cards">

                        <article class="approach-card">

                            <span class="card-number">
                                01
                            </span>

                            <i class="bi bi-search"></i>

                            <h4>
                                Understand
                            </h4>

                            <p>
                                We understand your business, technology
                                environment and challenges.
                            </p>

                        </article>


                        <article class="approach-card">

                            <span class="card-number">
                                02
                            </span>

                            <i class="bi bi-diagram-3"></i>

                            <h4>
                                Integrate
                            </h4>

                            <p>
                                We connect systems, data and infrastructure
                                into one efficient ecosystem.
                            </p>

                        </article>


                        <article class="approach-card">

                            <span class="card-number">
                                03
                            </span>

                            <i class="bi bi-graph-up-arrow"></i>

                            <h4>
                                Optimise
                            </h4>

                            <p>
                                We continuously improve performance,
                                security and scalability.
                            </p>

                        </article>


                        <article class="approach-card">

                            <span class="card-number">
                                04
                            </span>

                            <i class="bi bi-lightning-charge"></i>

                            <h4>
                                Accelerate
                            </h4>

                            <p>
                                We help businesses move faster with technology
                                that is ready for the future.
                            </p>

                        </article>

                    </div>

                </div>

            </div>

        </section>


        <!-- =====================================================
             DIFFERENCE
        ====================================================== -->

        <section class="difference-section">

            <div class="difference-bg"></div>

            <div class="why-container">

                <div class="difference-header">

                    <div>
                        <div class="section-label">
                            THE UNITECORE DIFFERENCE
                        </div>

                        <h2>
                            One core.
                            <span>Many possibilities.</span>
                        </h2>
                    </div>

                    <p>
                        From infrastructure and data to security and
                        analytics, we bring the right capabilities together
                        to create technology environments that are easier to
                        manage and ready to scale.
                    </p>

                </div>


                <div class="difference-grid">

                    <article class="difference-item">

                        <div class="difference-icon">
                            <i class="bi bi-layers"></i>
                        </div>

                        <h3>
                            Integrated Thinking
                        </h3>

                        <p>
                            We look beyond individual technologies and
                            understand how your complete technology ecosystem
                            works together.
                        </p>

                    </article>


                    <article class="difference-item">

                        <div class="difference-icon">
                            <i class="bi bi-shield-lock"></i>
                        </div>

                        <h3>
                            Security First
                        </h3>

                        <p>
                            Security is considered from the beginning,
                            helping protect your infrastructure, applications
                            and critical business data.
                        </p>

                    </article>


                    <article class="difference-item">

                        <div class="difference-icon">
                            <i class="bi bi-speedometer2"></i>
                        </div>

                        <h3>
                            Built to Scale
                        </h3>

                        <p>
                            Our solutions are designed to support changing
                            business requirements and future growth.
                        </p>

                    </article>


                    <article class="difference-item">

                        <div class="difference-icon">
                            <i class="bi bi-people"></i>
                        </div>

                        <h3>
                            People First
                        </h3>

                        <p>
                            Technology works best when it empowers people.
                            We focus on practical solutions that teams can
                            adopt and use effectively.
                        </p>

                    </article>

                </div>

            </div>

        </section>


        <!-- =====================================================
             JOURNEY
        ====================================================== -->

        <section class="journey-section" id="journey">

            <div class="why-container">

                <div class="section-heading centered">

                    <div class="section-label">
                        OUR JOURNEY
                    </div>

                    <h2>
                        Growing with
                        <span>our customers.</span>
                    </h2>

                    <p>
                        Our journey has been shaped by customers, technology
                        and a constant focus on building better solutions.
                    </p>

                </div>


                <div class="journey-timeline">

                    <div class="timeline-line"></div>


                    <div class="timeline-item">

                        <div class="timeline-year">
                            2015
                        </div>

                        <div class="timeline-dot"></div>

                        <div class="timeline-content">

                            <span>
                                THE BEGINNING
                            </span>

                            <h3>
                                4 Employees
                            </h3>

                            <p>
                                UniteCore began its journey with a focused
                                technology team and a vision for building
                                dependable IT solutions.
                            </p>

                        </div>

                    </div>


                    <div class="timeline-item reverse">

                        <div class="timeline-year">
                            2016–17
                        </div>

                        <div class="timeline-dot"></div>

                        <div class="timeline-content">

                            <span>
                                EARLY GROWTH
                            </span>

                            <h3>
                                30 Clients
                            </h3>

                            <p>
                                Strong customer relationships helped us
                                expand our capabilities and technology
                                expertise.
                            </p>

                        </div>

                    </div>


                    <div class="timeline-item">

                        <div class="timeline-year">
                            2018–19
                        </div>

                        <div class="timeline-dot"></div>

                        <div class="timeline-content">

                            <span>
                                EXPANSION
                            </span>

                            <h3>
                                ₹20 Cr Turnover
                            </h3>

                            <p>
                                Our growing customer base and expanding
                                technology portfolio accelerated business
                                growth.
                            </p>

                        </div>

                    </div>


                    <div class="timeline-item reverse">

                        <div class="timeline-year">
                            2020–21
                        </div>

                        <div class="timeline-dot"></div>

                        <div class="timeline-content">

                            <span>
                                RESILIENCE
                            </span>

                            <h3>
                                24 Employees
                            </h3>

                            <p>
                                We continued supporting customers while
                                strengthening our technology and service
                                capabilities.
                            </p>

                        </div>

                    </div>


                    <div class="timeline-item">

                        <div class="timeline-year">
                            2022–23
                        </div>

                        <div class="timeline-dot"></div>

                        <div class="timeline-content">

                            <span>
                                SCALE
                            </span>

                            <h3>
                                160+ Clients
                            </h3>

                            <p>
                                Our solutions reached more organisations
                                across industries and business environments.
                            </p>

                        </div>

                    </div>


                    <div class="timeline-item reverse">

                        <div class="timeline-year">
                            2023–24
                        </div>

                        <div class="timeline-dot"></div>

                        <div class="timeline-content">

                            <span>
                                MOMENTUM
                            </span>

                            <h3>
                                ₹35 Cr Turnover
                            </h3>

                            <p>
                                Continued customer growth strengthened
                                UniteCore's position as a technology partner.
                            </p>

                        </div>

                    </div>


                    <div class="timeline-item">

                        <div class="timeline-year">
                            2025
                        </div>

                        <div class="timeline-dot"></div>

                        <div class="timeline-content">

                            <span>
                                TODAY
                            </span>

                            <h3>
                                100+ Employees
                            </h3>

                            <div class="timeline-stats">

                                <div>
                                    <strong>550+</strong>
                                    <span>Clients</span>
                                </div>

                                <div>
                                    <strong>₹50 Cr</strong>
                                    <span>Turnover</span>
                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </div>

        </section>


        <!-- =====================================================
             TEAM
        ====================================================== -->

        <section class="team-section">

            <div class="why-container">

                <div class="team-grid">

                    <div class="team-content">

                        <div class="section-label">
                            OUR PEOPLE
                        </div>

                        <h2>
                            Technology is powerful.
                            <span>People make it matter.</span>
                        </h2>

                        <p>
                            Our people bring together technology expertise,
                            industry understanding and a customer-first
                            mindset.
                        </p>

                        <p>
                            We work collaboratively across teams to solve
                            complex technology challenges and deliver
                            practical business outcomes.
                        </p>

                        <div class="team-highlight">

                            <i class="bi bi-people-fill"></i>

                            <div>
                                <strong>
                                    People First
                                </strong>

                                <span>
                                    A culture built around collaboration,
                                    ownership and continuous learning.
                                </span>
                            </div>

                        </div>

                    </div>


                    <div class="team-visual">

                        <div class="team-circle team-circle-one"></div>
                        <div class="team-circle team-circle-two"></div>

                        <div class="team-center">
                            <i class="bi bi-people"></i>
                        </div>

                        <div class="team-node node-one">
                            <i class="bi bi-code-slash"></i>
                        </div>

                        <div class="team-node node-two">
                            <i class="bi bi-shield-check"></i>
                        </div>

                        <div class="team-node node-three">
                            <i class="bi bi-database"></i>
                        </div>

                        <div class="team-node node-four">
                            <i class="bi bi-bar-chart"></i>
                        </div>

                    </div>

                </div>

            </div>

        </section>


        <!-- =====================================================
             PROCESS
        ====================================================== -->

        <section class="process-section">

            <div class="why-container">

                <div class="section-heading centered">

                    <div class="section-label">
                        HOW WE WORK
                    </div>

                    <h2>
                        From challenge
                        <span>to outcome.</span>
                    </h2>

                    <p>
                        A structured approach helps us turn complex technology
                        challenges into measurable business outcomes.
                    </p>

                </div>


                <div class="process-grid">

                    <article class="process-card">

                        <span class="process-number">
                            01
                        </span>

                        <i class="bi bi-chat-square-text"></i>

                        <h3>
                            Discover
                        </h3>

                        <p>
                            Understand business objectives, existing
                            infrastructure and technology challenges.
                        </p>

                    </article>


                    <article class="process-card">

                        <span class="process-number">
                            02
                        </span>

                        <i class="bi bi-pencil-square"></i>

                        <h3>
                            Design
                        </h3>

                        <p>
                            Develop the right architecture, strategy and
                            technology roadmap.
                        </p>

                    </article>


                    <article class="process-card">

                        <span class="process-number">
                            03
                        </span>

                        <i class="bi bi-gear-wide-connected"></i>

                        <h3>
                            Implement
                        </h3>

                        <p>
                            Deploy and integrate solutions with minimal
                            disruption to your business.
                        </p>

                    </article>


                    <article class="process-card">

                        <span class="process-number">
                            04
                        </span>

                        <i class="bi bi-arrow-repeat"></i>

                        <h3>
                            Optimise
                        </h3>

                        <p>
                            Monitor, improve and evolve the technology
                            environment as your business grows.
                        </p>

                    </article>

                </div>

            </div>

        </section>


        <!-- =====================================================
             ECOSYSTEM
        ====================================================== -->

        <section class="ecosystem-section">

            <div class="ecosystem-glow"></div>

            <div class="why-container">

                <div class="ecosystem-grid">

                    <div class="ecosystem-content">

                        <div class="section-label">
                            TECHNOLOGY ECOSYSTEM
                        </div>

                        <h2>
                            Connected technology.
                            <span>Better outcomes.</span>
                        </h2>

                        <p>
                            UniteCore brings together infrastructure,
                            cloud, security, data, networking and analytics
                            capabilities to create a connected technology
                            ecosystem.
                        </p>

                        <p>
                            This integrated approach helps organisations
                            simplify their technology environment while
                            preparing for future growth.
                        </p>

                        <div class="ecosystem-tags">

                            <span>
                                Cloud
                            </span>

                            <span>
                                Security
                            </span>

                            <span>
                                Data
                            </span>

                            <span>
                                Networking
                            </span>

                            <span>
                                Analytics
                            </span>

                            <span>
                                Infrastructure
                            </span>

                        </div>

                    </div>


                    <div class="ecosystem-visual">

                        <div class="eco-core">
                            <span>UNITE</span>
                            <strong>CORE</strong>
                        </div>

                        <div class="eco-line eco-line-one"></div>
                        <div class="eco-line eco-line-two"></div>
                        <div class="eco-line eco-line-three"></div>
                        <div class="eco-line eco-line-four"></div>
                        <div class="eco-line eco-line-five"></div>
                        <div class="eco-line eco-line-six"></div>

                        <div class="eco-node eco-one">
                            <i class="bi bi-cloud"></i>
                            <span>Cloud</span>
                        </div>

                        <div class="eco-node eco-two">
                            <i class="bi bi-shield-lock"></i>
                            <span>Security</span>
                        </div>

                        <div class="eco-node eco-three">
                            <i class="bi bi-database"></i>
                            <span>Data</span>
                        </div>

                        <div class="eco-node eco-four">
                            <i class="bi bi-diagram-3"></i>
                            <span>Network</span>
                        </div>

                        <div class="eco-node eco-five">
                            <i class="bi bi-bar-chart"></i>
                            <span>Analytics</span>
                        </div>

                        <div class="eco-node eco-six">
                            <i class="bi bi-hdd-stack"></i>
                            <span>Infrastructure</span>
                        </div>

                    </div>

                </div>

            </div>

        </section>


        <!-- =====================================================
             STATISTICS
        ====================================================== -->

        <section class="why-statistics">

            <StatisticsSection />

        </section>


        <!-- =====================================================
             FINAL CTA
        ====================================================== -->

        <section class="final-cta">

            <div class="final-cta-grid"></div>
            <div class="final-cta-glow"></div>

            <div class="why-container">

                <div class="final-cta-content">

                    <div class="section-label">
                        LET'S BUILD TOGETHER
                    </div>

                    <h2>
                        Ready to make your
                        <span>technology work smarter?</span>
                    </h2>

                    <p>
                        Talk to our experts about your technology,
                        infrastructure, security or data requirements.
                    </p>

                    <router-link to="/contact-us" class="cta-button">
                        <span>Talk to an Expert</span>

                    </router-link>

                </div>

            </div>

        </section>


        <!-- =====================================================
             FOOTER
        ====================================================== -->

        <FooterSection />

    </main>
</template>



<script>
import HeaderSection from "@/components/HeaderSection.vue";
import StatisticsSection from "@/components/StatisticsSection.vue";
import FooterSection from "@/components/FooterSection.vue";

export default {
    name: "WhyUniteCore",

    components: {
        HeaderSection,
        StatisticsSection,
        FooterSection
    },

    data() {
        return {
            publicBaseUrl: process.env.BASE_URL
        };
    },

    mounted() {
        this.handleHashScroll();
    },

    methods: {
        handleHashScroll() {
            const hash = window.location.hash;

            if (!hash) {
                window.scrollTo({
                    top: 0,
                    behavior: "auto"
                });
                return;
            }

            // Ignore Vue Router's route hash, such as #/WhyUniteCore.
            if (hash.startsWith("#/")) {
                return;
            }

            const targetId = decodeURIComponent(hash.substring(1));

            if (!targetId) {
                return;
            }

            this.$nextTick(() => {
                const element = document.getElementById(targetId);

                if (!element) {
                    return;
                }

                setTimeout(() => {
                    const headerOffset = 90;

                    const elementPosition =
                        element.getBoundingClientRect().top +
                        window.pageYOffset;

                    const offsetPosition =
                        elementPosition - headerOffset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth"
                    });
                }, 100);
            });
        }
    }
};
</script>


<style scoped>
/* ============================================================
   GLOBAL
============================================================ */

.why-page {
    position: relative;
    overflow: hidden;
    background: #ffffff;
    color: #17201d;
}

.why-container {
    width: min(1240px, calc(100% - 50px));
    margin: 0 auto;
}

.section-label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: #f5820b;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: .18em;
    text-transform: uppercase;
}

.section-label::before {
    content: "";
    width: 28px;
    height: 2px;
    background: #f5820b;
}

.section-heading {
    max-width: 760px;
}

.section-heading.centered {
    margin-left: auto;
    margin-right: auto;
    text-align: center;
}

.section-heading.centered .section-label {
    justify-content: center;
}

.section-heading h2,
.difference-header h2,
.team-content h2,
.ecosystem-content h2 {
    margin: 18px 0 18px;
    color: #102e27;
    font-size: clamp(38px, 5vw, 68px);
    line-height: .98;
    letter-spacing: -.045em;
    font-weight: 800;
}

.section-heading h2 span,
.difference-header h2 span,
.team-content h2 span,
.ecosystem-content h2 span {
    color: #f5820b;
}

.section-heading p,
.difference-header p,
.team-content p,
.ecosystem-content p {
    color: #333835;
    font-size: 16px;
    line-height: 1.8;
}


/* ============================================================
   HERO
============================================================ */

.why-hero {
    position: relative;
    min-height: 850px;
    display: flex;
    align-items: center;
    overflow: hidden;

    background:
        radial-gradient(circle at 80% 50%,
            rgba(245, 130, 11, .16),
            transparent 30%),
        #102e27;

    color: #ffffff;
}

.hero-grid {
    position: absolute;
    inset: 0;
    opacity: .18;

    background-image:
        linear-gradient(rgba(255, 255, 255, .07) 1px,
            transparent 1px),
        linear-gradient(90deg,
            rgba(255, 255, 255, .07) 1px,
            transparent 1px);

    background-size: 60px 60px;

    mask-image:
        linear-gradient(90deg,
            black,
            transparent 90%);
}

.hero-glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(70px);
    pointer-events: none;
}

.hero-glow-one {
    width: 420px;
    height: 420px;
    right: 5%;
    top: 20%;
    background: rgba(245, 130, 11, .18);
}

.hero-glow-two {
    width: 300px;
    height: 300px;
    left: 20%;
    bottom: -120px;
    background: rgba(0, 217, 192, .08);
}

.why-hero .why-container {
    position: relative;
    z-index: 2;

    display: grid;
    grid-template-columns: 1fr 1fr;

    align-items: center;

    gap: 70px;

    padding-top: 100px;
    padding-bottom: 80px;
}

.hero-content {
    max-width: 670px;
}

.eyebrow {
    display: flex;
    align-items: center;
    gap: 12px;

    color: #f5820b;

    font-size: 13px;
    font-weight: 800;
    letter-spacing: .2em;
}

.eyebrow-line {
    width: 35px;
    height: 2px;
    background: #f5820b;
}

.hero-content h1 {
    margin: 22px 0 25px;

    font-size: clamp(52px, 6.5vw, 88px);
    line-height: .92;
    letter-spacing: -.055em;
    font-weight: 800;
}

.hero-content h1 span {
    display: block;
    color: #f5820b;
}

.hero-description {
    max-width: 590px;
    margin: 0;

    color: rgba(255, 255, 255, .72);

    font-size: 17px;
    line-height: 1.8;
}

.hero-actions {
    display: flex;
    align-items: center;
    gap: 14px;

    margin-top: 35px;
}

.hero-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;

    gap: 12px;

    min-height: 52px;

    padding: 0 22px;

    border-radius: 5px;

    text-decoration: none;

    font-size: 13px;
    font-weight: 800;

    transition:
        transform .25s ease,
        background .25s ease,
        color .25s ease,
        border-color .25s ease;
}

.hero-btn:hover {
    transform: translateY(-3px);
}

.hero-btn-primary {
    background: #f5820b;
    color: #111;
}

.hero-btn-primary:hover {
    background: #ff9f2f;
    color: #111;
}

.hero-btn-secondary {
    border: 1px solid rgba(255, 255, 255, .25);
    color: #ffffff;
}

.hero-btn-secondary:hover {
    border-color: #f5820b;
    color: #f5820b;
}

.hero-meta {
    display: flex;
    flex-wrap: wrap;

    gap: 35px;

    margin-top: 55px;
    padding-top: 25px;

    border-top: 1px solid rgba(255, 255, 255, .12);
}

.hero-meta-item {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.hero-meta-item strong {
    color: #ffffff;
    font-size: 27px;
    line-height: 1;
}

.hero-meta-item span {
    color: rgba(255, 255, 255, .48);

    font-size: 13px;
    letter-spacing: .06em;

    text-transform: uppercase;
}


/* ============================================================
   HERO VISUAL
============================================================ */

.hero-visual {
    position: relative;

    width: min(100%, 560px);

    aspect-ratio: 1;

    margin-left: auto;
}

.orbit {
    position: absolute;

    left: 50%;
    top: 50%;

    border: 1px solid rgba(245, 130, 11, .24);
    border-radius: 50%;

    transform: translate(-50%, -50%);
}

.orbit-one {
    width: 48%;
    height: 48%;
}

.orbit-two {
    width: 70%;
    height: 70%;

    border-color: rgba(245, 130, 11, .16);
}

.orbit-three {
    width: 92%;
    height: 92%;

    border-color: rgba(255, 255, 255, .09);
}

.core-circle {
    position: absolute;

    left: 50%;
    top: 50%;

    width: 175px;
    height: 175px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 50%;

    transform: translate(-50%, -50%);

    background:
        radial-gradient(circle,
            rgba(245, 130, 11, .5),
            rgba(245, 130, 11, .08) 55%,
            transparent 70%);

    box-shadow:
        0 0 80px rgba(245, 130, 11, .25),
        inset 0 0 40px rgba(245, 130, 11, .2);
}

.core-inner {
    width: 110px;
    height: 110px;

    display: flex;
    align-items: center;
    justify-content: center;

    border: 1px solid rgba(245, 130, 11, .5);

    border-radius: 50%;

    background: #102e27;

    box-shadow:
        0 0 35px rgba(245, 130, 11, .2);
}

.core-inner span {
    color: #f5820b;

    font-size: 30px;
    font-weight: 900;

    letter-spacing: -.05em;
}

.floating-card {
    position: absolute;

    display: flex;
    align-items: center;

    gap: 12px;

    min-width: 155px;

    padding: 13px 15px;

    border: 1px solid rgba(255, 255, 255, .1);

    border-radius: 10px;

    background: rgba(255, 255, 255, .06);

    backdrop-filter: blur(15px);

    box-shadow:
        0 20px 50px rgba(0, 0, 0, .22);

    animation:
        floatingCard 4s ease-in-out infinite;
}

.floating-card strong {
    display: block;

    color: #ffffff;

    font-size: 12px;
}

.floating-card span {
    display: block;

    margin-top: 3px;

    color: rgba(255, 255, 255, .45);

    font-size: 12px;
}

.card-icon {
    width: 36px;
    height: 36px;

    flex: 0 0 36px;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 8px;

    background: rgba(245, 130, 11, .12);

    color: #f5820b;
}

.card-one {
    top: 12%;
    left: 5%;
}

.card-two {
    top: 22%;
    right: 0;

    animation-delay: .7s;
}

.card-three {
    bottom: 20%;
    left: 0;

    animation-delay: 1.3s;
}

.card-four {
    bottom: 8%;
    right: 5%;

    animation-delay: 2s;
}

@keyframes floatingCard {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-9px);
    }
}


/* ============================================================
   APPROACH
============================================================ */

.approach-section {
    padding: 125px 0;
    background: #ffffff;
}

.approach-grid {
    display: grid;

    grid-template-columns: .85fr 1.15fr;

    gap: 80px;

    margin-top: 70px;
}

.approach-main {
    position: relative;

    padding: 40px;

    border-radius: 12px;

    background: #102e27;

    color: #ffffff;

    overflow: hidden;
}

.approach-main::after {
    content: "";

    position: absolute;

    width: 250px;
    height: 250px;

    right: -100px;
    bottom: -120px;

    border-radius: 50%;

    background: rgba(245, 130, 11, .2);

    filter: blur(30px);
}

.approach-number {
    color: #f5820b;

    font-size: 12px;
    font-weight: 900;
    letter-spacing: .1em;
}

.approach-main h3 {
    margin: 45px 0 20px;

    font-size: clamp(32px, 4vw, 52px);

    line-height: 1;

    letter-spacing: -.04em;
}

.approach-main h3 span {
    display: block;
    color: #f5820b;
}

.approach-main p {
    max-width: 470px;

    color: rgba(255, 255, 255, .62);

    font-size: 15px;
    line-height: 1.8;
}

.approach-line {
    width: 70px;
    height: 2px;

    margin-top: 45px;

    background: #f5820b;
}

.approach-cards {
    display: grid;

    grid-template-columns: repeat(2, 1fr);

    gap: 18px;
}

.approach-card {
    position: relative;

    padding: 30px;

    border: 1px solid #e8ecea;

    border-radius: 10px;

    background: #f8faf9;

    transition:
        transform .25s ease,
        border-color .25s ease,
        box-shadow .25s ease;
}

.approach-card:hover {
    transform: translateY(-7px);

    border-color: rgba(245, 130, 11, .4);

    box-shadow:
        0 20px 45px rgba(16, 46, 39, .08);
}

.card-number {
    position: absolute;

    top: 20px;
    right: 20px;

    color: #cbd4d0;

    font-size: 13px;
    font-weight: 800;
}

.approach-card>i {
    display: block;

    margin-bottom: 25px;

    color: #f5820b;

    font-size: 28px;
}

.approach-card h4 {
    margin: 0 0 10px;

    color: #102e27;

    font-size: 19px;
}

.approach-card p {
    margin: 0;

    color: #333835;

    font-size: 13px;
    line-height: 1.7;
}


/* ============================================================
   DIFFERENCE
============================================================ */

.difference-section {
    position: relative;

    padding: 125px 0;

    overflow: hidden;

    background: #edf4f1;
}

.difference-bg {
    position: absolute;

    width: 500px;
    height: 500px;

    right: -200px;
    top: -200px;

    border-radius: 50%;

    background: rgba(245, 130, 11, .08);

    filter: blur(50px);
}

.difference-header {
    position: relative;

    z-index: 1;

    display: grid;

    grid-template-columns: 1fr .75fr;

    gap: 100px;

    align-items: end;
}

.difference-header p {
    margin: 0;
}

.difference-grid {
    position: relative;

    z-index: 1;

    display: grid;

    grid-template-columns: repeat(4, 1fr);

    gap: 18px;

    margin-top: 65px;
}

.difference-item {
    padding: 35px 25px;

    border-top: 2px solid #d3ddd8;

    transition:
        transform .25s ease,
        border-color .25s ease;
}

.difference-item:hover {
    transform: translateY(-5px);

    border-color: #f5820b;
}

.difference-icon {
    width: 48px;
    height: 48px;

    display: flex;
    align-items: center;
    justify-content: center;

    margin-bottom: 25px;

    border-radius: 50%;

    background: #ffffff;

    color: #f5820b;

    font-size: 20px;
}

.difference-item h3 {
    margin: 0 0 12px;

    color: #102e27;

    font-size: 19px;
}

.difference-item p {
    margin: 0;

    color: #333835;

    font-size: 13px;

    line-height: 1.75;
}


/* ============================================================
   JOURNEY
============================================================ */

.journey-section {
    padding: 130px 0;

    background: #ffffff;
}

.journey-timeline {
    position: relative;

    max-width: 1000px;

    margin: 80px auto 0;
}

.timeline-line {
    position: absolute;

    top: 0;
    bottom: 0;

    left: 50%;

    width: 1px;

    background: #dce4e0;

    transform: translateX(-50%);
}

.timeline-item {
    position: relative;

    display: grid;

    grid-template-columns: 1fr 50px 1fr;

    align-items: center;

    min-height: 190px;
}

.timeline-year {
    padding-right: 50px;

    color: #f5820b;

    font-size: 24px;
    font-weight: 900;

    text-align: right;
}

.timeline-item.reverse .timeline-year {
    grid-column: 3;
    grid-row: 1;

    padding-right: 0;
    padding-left: 50px;

    text-align: left;
}

.timeline-dot {
    position: relative;

    z-index: 2;

    width: 14px;
    height: 14px;

    margin: auto;

    border: 3px solid #ffffff;

    border-radius: 50%;

    background: #f5820b;

    box-shadow:
        0 0 0 5px rgba(245, 130, 11, .12);
}

.timeline-content {
    padding-left: 50px;
}

.timeline-item.reverse .timeline-content {
    grid-column: 1;
    grid-row: 1;

    padding-left: 0;
    padding-right: 50px;

    text-align: right;
}

.timeline-content>span {
    color: #89948f;

    font-size: 12px;
    font-weight: 800;

    letter-spacing: .15em;
}

.timeline-content h3 {
    margin: 8px 0 10px;

    color: #102e27;

    font-size: 25px;
}

.timeline-content p {
    margin: 0;

    color: #333835;

    font-size: 13px;

    line-height: 1.7;
}

.timeline-stats {
    display: flex;

    gap: 25px;

    justify-content: flex-start;

    margin-top: 15px;
}

.timeline-item.reverse .timeline-stats {
    justify-content: flex-end;
}

.timeline-stats div {
    display: flex;
    flex-direction: column;
}

.timeline-stats strong {
    color: #f5820b;

    font-size: 21px;
}

.timeline-stats span {
    color: #333835;

    font-size: 13px;
}


/* ============================================================
   TEAM
============================================================ */

.team-section {
    padding: 125px 0;

    background: #102e27;

    color: #ffffff;
}

.team-grid {
    display: grid;

    grid-template-columns: 1fr 1fr;

    gap: 90px;

    align-items: center;
}

.team-content h2 {
    color: #ffffff;
}

.team-content p {
    max-width: 580px;

    color: rgba(255, 255, 255, .62);
}

.team-highlight {
    display: flex;
    align-items: center;

    gap: 18px;

    max-width: 500px;

    margin-top: 35px;
    padding: 20px;

    border-left: 2px solid #f5820b;

    background: rgba(255, 255, 255, .04);
}

.team-highlight>i {
    color: #f5820b;

    font-size: 26px;
}

.team-highlight strong,
.team-highlight span {
    display: block;
}

.team-highlight strong {
    margin-bottom: 5px;

    color: #ffffff;

    font-size: 14px;
}

.team-highlight span {
    color: rgba(255, 255, 255, .5);

    font-size: 12px;

    line-height: 1.5;
}

.team-visual {
    position: relative;

    width: min(100%, 500px);

    aspect-ratio: 1;

    margin: auto;
}

.team-circle {
    position: absolute;

    left: 50%;
    top: 50%;

    border: 1px solid rgba(245, 130, 11, .2);

    border-radius: 50%;

    transform: translate(-50%, -50%);
}

.team-circle-one {
    width: 65%;
    height: 65%;
}

.team-circle-two {
    width: 90%;
    height: 90%;
}

.team-center {
    position: absolute;

    left: 50%;
    top: 50%;

    width: 115px;
    height: 115px;

    display: flex;
    align-items: center;
    justify-content: center;

    border: 1px solid rgba(245, 130, 11, .5);

    border-radius: 50%;

    transform: translate(-50%, -50%);

    background: rgba(245, 130, 11, .08);

    color: #f5820b;

    font-size: 38px;
}

.team-node {
    position: absolute;

    width: 55px;
    height: 55px;

    display: flex;
    align-items: center;
    justify-content: center;

    border: 1px solid rgba(255, 255, 255, .12);

    border-radius: 50%;

    background: rgba(255, 255, 255, .05);

    color: #f5820b;

    font-size: 18px;
}

.node-one {
    left: 20%;
    top: 23%;
}

.node-two {
    right: 18%;
    top: 25%;
}

.node-three {
    left: 17%;
    bottom: 24%;
}

.node-four {
    right: 17%;
    bottom: 23%;
}


/* ============================================================
   PROCESS
============================================================ */

.process-section {
    padding: 125px 0;

    background: #ffffff;
}

.process-grid {
    display: grid;

    grid-template-columns: repeat(4, 1fr);

    gap: 18px;

    margin-top: 70px;
}

.process-card {
    position: relative;

    min-height: 310px;

    padding: 35px 28px;

    border: 1px solid #e7ece9;

    border-radius: 10px;

    background: #ffffff;

    transition:
        transform .25s ease,
        box-shadow .25s ease,
        border-color .25s ease;
}

.process-card:hover {
    transform: translateY(-7px);

    border-color: rgba(245, 130, 11, .4);

    box-shadow:
        0 25px 55px rgba(16, 46, 39, .08);
}

.process-number {
    position: absolute;

    top: 22px;
    right: 22px;

    color: #cbd4d0;

    font-size: 13px;
    font-weight: 900;
}

.process-card>i {
    display: block;

    margin-top: 35px;
    margin-bottom: 28px;

    color: #f5820b;

    font-size: 30px;
}

.process-card h3 {
    margin: 0 0 12px;

    color: #102e27;

    font-size: 20px;
}

.process-card p {
    margin: 0;

    color: #333835;

    font-size: 13px;

    line-height: 1.75;
}


/* ============================================================
   ECOSYSTEM
============================================================ */

.ecosystem-section {
    position: relative;

    padding: 130px 0;

    overflow: hidden;

    background: #edf4f1;
}

.ecosystem-glow {
    position: absolute;

    width: 600px;
    height: 600px;

    right: -200px;
    top: 50%;

    border-radius: 50%;

    transform: translateY(-50%);

    background: rgba(245, 130, 11, .08);

    filter: blur(70px);
}

.ecosystem-grid {
    position: relative;

    z-index: 1;

    display: grid;

    grid-template-columns: .9fr 1.1fr;

    gap: 70px;

    align-items: center;
}

.ecosystem-tags {
    display: flex;

    flex-wrap: wrap;

    gap: 8px;

    margin-top: 30px;
}

.ecosystem-tags span {
    padding: 9px 13px;

    border: 1px solid #d5dfda;

    border-radius: 4px;

    background: rgba(255, 255, 255, .6);

    color: #52615b;

    font-size: 13px;
    font-weight: 700;
}

.ecosystem-visual {
    position: relative;

    width: min(100%, 560px);

    aspect-ratio: 1;

    margin: auto;
}

.eco-core {
    position: absolute;

    left: 50%;
    top: 50%;

    width: 135px;
    height: 135px;

    display: flex;

    flex-direction: column;

    align-items: center;
    justify-content: center;

    border: 2px solid #f5820b;

    border-radius: 50%;

    transform: translate(-50%, -50%);

    background: #102e27;

    box-shadow:
        0 0 60px rgba(245, 130, 11, .2);
}

.eco-core span {
    color: rgba(255, 255, 255, .6);

    font-size: 12px;

    letter-spacing: .2em;
}

.eco-core strong {
    color: #f5820b;

    font-size: 25px;
}

.eco-node {
    position: absolute;

    z-index: 3;

    width: 75px;
    height: 75px;

    display: flex;

    flex-direction: column;

    align-items: center;
    justify-content: center;

    gap: 5px;

    border: 1px solid rgba(16, 46, 39, .12);

    border-radius: 50%;

    background: #ffffff;

    color: #102e27;

    box-shadow:
        0 15px 35px rgba(16, 46, 39, .08);
}

.eco-node i {
    color: #f5820b;

    font-size: 18px;
}

.eco-node span {
    font-size: 12px;

    font-weight: 800;
}

.eco-one {
    left: 43%;
    top: 1%;
}

.eco-two {
    right: 8%;
    top: 22%;
}

.eco-three {
    right: 5%;
    bottom: 20%;
}

.eco-four {
    left: 42%;
    bottom: 1%;
}

.eco-five {
    left: 6%;
    bottom: 20%;
}

.eco-six {
    left: 5%;
    top: 22%;
}

.eco-line {
    position: absolute;

    left: 50%;
    top: 50%;

    width: 43%;
    height: 1px;

    transform-origin: left center;

    background: rgba(245, 130, 11, .25);
}

.eco-line-one {
    transform: rotate(-90deg);
}

.eco-line-two {
    transform: rotate(-30deg);
}

.eco-line-three {
    transform: rotate(30deg);
}

.eco-line-four {
    transform: rotate(90deg);
}

.eco-line-five {
    transform: rotate(150deg);
}

.eco-line-six {
    transform: rotate(210deg);
}


/* ============================================================
   STATISTICS
============================================================ */

.why-statistics {
    position: relative;

    background: #ffffff;

    overflow: hidden;
}


/* ============================================================
   FINAL CTA
============================================================ */

.final-cta {
    position: relative;

    overflow: hidden;

    padding: 110px 0;

    background: #102e27;

    color: #ffffff;
}

.final-cta-grid {
    position: absolute;

    inset: 0;

    opacity: .12;

    background-image:
        linear-gradient(rgba(255, 255, 255, .08) 1px,
            transparent 1px),
        linear-gradient(90deg,
            rgba(255, 255, 255, .08) 1px,
            transparent 1px);

    background-size: 55px 55px;
}

.final-cta-glow {
    position: absolute;

    width: 500px;
    height: 500px;

    right: -100px;
    top: -250px;

    border-radius: 50%;

    background: rgba(245, 130, 11, .16);

    filter: blur(60px);
}

.final-cta-content {
    position: relative;

    z-index: 2;

    max-width: 850px;
}

.final-cta-content h2 {
    margin: 18px 0;

    font-size: clamp(40px, 6vw, 75px);

    line-height: .95;

    letter-spacing: -.05em;
}

.final-cta-content h2 span {
    display: block;

    color: #f5820b;
}

.final-cta-content p {
    max-width: 600px;

    color: rgba(255, 255, 255, .62);

    font-size: 16px;

    line-height: 1.8;
}

.cta-button {
    display: inline-flex;

    align-items: center;

    gap: 13px;

    margin-top: 25px;

    padding: 15px 22px;

    border-radius: 5px;

    background: #f5820b;

    color: #111111;

    text-decoration: none;

    font-size: 13px;

    font-weight: 800;

    transition:
        transform .25s ease,
        background .25s ease;
}

.cta-button:hover {
    transform: translateY(-3px);

    background: #ff9f2f;

    color: #111111;
}


/* ============================================================
   TABLET
============================================================ */

@media (max-width: 1100px) {

    .why-hero .why-container {
        gap: 35px;
    }

    .difference-grid,
    .process-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .difference-header {
        gap: 50px;
    }

    .approach-grid,
    .ecosystem-grid {
        gap: 45px;
    }

}


/* ============================================================
   TABLET / SMALL LAPTOP
============================================================ */

@media (max-width: 900px) {

    .why-container {
        width: calc(100% - 35px);
    }

    .why-hero {
        min-height: auto;
    }

    .why-hero .why-container {
        grid-template-columns: 1fr;

        padding-top: 150px;
        padding-bottom: 80px;
    }

    .hero-content {
        max-width: 720px;
    }

    /*
       IMPORTANT:
       Do NOT use transform: scale() here.
       The visual needs to remain responsive naturally.
    */
    .hero-visual {
        width: min(100%, 520px);

        aspect-ratio: 1;

        margin: 20px auto 0;
    }

    .approach-grid,
    .team-grid,
    .ecosystem-grid,
    .difference-header {
        grid-template-columns: 1fr;
    }

    .difference-header {
        gap: 20px;
    }

    .timeline-item {
        grid-template-columns: 70px 35px 1fr;

        min-height: 180px;
    }

    .timeline-year {
        padding-right: 10px;

        font-size: 17px;
    }

    .timeline-item.reverse .timeline-year {
        grid-column: 1;
        grid-row: 1;

        padding-left: 0;
        padding-right: 10px;

        text-align: right;
    }

    .timeline-content,
    .timeline-item.reverse .timeline-content {
        grid-column: 3;
        grid-row: 1;

        padding-left: 20px;
        padding-right: 0;

        text-align: left;
    }

    .timeline-item.reverse .timeline-stats {
        justify-content: flex-start;
    }

    .timeline-line {
        left: 87px;
    }

}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 600px) {

    .why-page {
        width: 100%;
        overflow-x: hidden;
    }

    .why-container {
        width: calc(100% - 28px);
        max-width: 100%;
    }

    /* --------------------------------------------------------
       HERO
    -------------------------------------------------------- */

    .why-hero {
        width: 100%;

        min-height: auto;

        align-items: flex-start;

        padding: 110px 0 35px;

        overflow: hidden;

        background:
            radial-gradient(circle at 85% 25%,
                rgba(245, 130, 11, .18),
                transparent 35%),
            #102e27;
    }

    .hero-grid {
        opacity: .10;

        background-size: 38px 38px;

        mask-image:
            linear-gradient(180deg,
                black,
                transparent 95%);
    }

    .hero-glow-one {
        width: 240px;
        height: 240px;

        right: -100px;
        top: 15%;

        filter: blur(55px);
    }

    .hero-glow-two {
        width: 180px;
        height: 180px;

        left: -80px;
        bottom: -70px;

        filter: blur(50px);
    }

    .why-hero .why-container {
        display: flex;

        flex-direction: column;

        gap: 0;

        padding-top: 0;
        padding-bottom: 0;
    }

    .hero-content {
        width: 100%;
        max-width: none;

        text-align: left;
    }

    .eyebrow {
        gap: 9px;

        font-size: 12px;

        letter-spacing: .15em;
    }

    .eyebrow-line {
        width: 25px;
        height: 2px;
    }

    .hero-content h1 {
        margin: 18px 0 18px;

        font-size: clamp(40px, 12vw, 54px);

        line-height: .95;

        letter-spacing: -.05em;
    }

    .hero-content h1 span {
        display: block;
    }

    .hero-description {
        width: 100%;
        max-width: none;

        font-size: 14px;

        line-height: 1.7;
    }

    /* --------------------------------------------------------
       BUTTONS
    -------------------------------------------------------- */

    .hero-actions {
        width: 100%;

        display: flex;

        flex-direction: column;

        align-items: stretch;

        gap: 10px;

        margin-top: 26px;
    }

    .hero-btn {
        width: 100%;

        min-height: 50px;

        padding: 0 18px;

        font-size: 12px;
    }

    /* --------------------------------------------------------
       STATS
    -------------------------------------------------------- */

    .hero-meta {
        width: 100%;

        display: grid;

        grid-template-columns: repeat(2, minmax(0, 1fr));

        gap: 0;

        margin-top: 30px;

        padding-top: 20px;

        border-top:
            1px solid rgba(255, 255, 255, .12);
    }

    .hero-meta-item {
        min-width: 0;

        padding: 10px 8px;
    }

    .hero-meta-item:nth-child(odd) {
        border-right:
            1px solid rgba(255, 255, 255, .10);
    }

    .hero-meta-item:nth-child(n + 3) {
        border-top:
            1px solid rgba(255, 255, 255, .10);
    }

    .hero-meta-item strong {
        font-size: 23px;
    }

    .hero-meta-item span {
        font-size: 12px;

        line-height: 1.4;

        letter-spacing: .05em;
    }

    /* --------------------------------------------------------
       HERO VISUAL
       
       IMPORTANT:
       No transform scale here.
       We resize the actual visual instead.
    -------------------------------------------------------- */

    .hero-visual {
        position: relative;

        width: 100%;

        height: 330px;

        min-height: 330px;

        aspect-ratio: auto;

        margin: 15px auto 0;

        overflow: visible;
    }

    /* --------------------------------------------------------
       ORBITS
    -------------------------------------------------------- */

    .orbit-one {
        width: 215px;
        height: 215px;
    }

    .orbit-two {
        width: 285px;
        height: 285px;
    }

    .orbit-three {
        width: 345px;
        height: 190px;

        transform:
            translate(-50%, -50%) rotate(-12deg);
    }

    /* --------------------------------------------------------
       CORE
    -------------------------------------------------------- */

    .core-circle {
        width: 125px;
        height: 125px;
    }

    .core-inner {
        width: 78px;
        height: 78px;
    }

    .core-inner span {
        font-size: 22px;
    }

    /* --------------------------------------------------------
       FLOATING CARDS
    -------------------------------------------------------- */

    .floating-card {
        z-index: 5;

        width: 118px;

        min-width: 118px;
        max-width: 118px;

        padding: 8px 9px;

        gap: 7px;

        border-radius: 8px;
    }

    .floating-card .card-icon {
        width: 27px;
        height: 27px;

        flex: 0 0 27px;

        border-radius: 6px;

        font-size: 13px;
    }

    .floating-card strong {
        font-size: 12px;

        line-height: 1.15;
    }

    .floating-card span {
        margin-top: 2px;

        font-size: 6.5px;

        line-height: 1.2;
    }

    .card-one {
        top: 30px;
        left: 0;
    }

    .card-two {
        top: 62px;
        right: 0;
    }

    .card-three {
        left: 0;
        bottom: 48px;
    }

    .card-four {
        right: 0;
        bottom: 20px;
    }

    /* --------------------------------------------------------
       OTHER SECTIONS
    -------------------------------------------------------- */

    .approach-section,
    .difference-section,
    .journey-section,
    .team-section,
    .process-section,
    .ecosystem-section {
        padding: 85px 0;
    }

    .approach-grid {
        margin-top: 45px;
    }

    .approach-cards,
    .difference-grid,
    .process-grid {
        grid-template-columns: 1fr;
    }

    .approach-main {
        padding: 30px;
    }

    .approach-main h3 {
        margin-top: 35px;

        font-size: 38px;
    }

    .approach-card {
        padding: 25px;
    }

    .difference-header h2,
    .team-content h2,
    .ecosystem-content h2,
    .section-heading h2 {
        font-size: 42px;
    }

    .difference-grid {
        margin-top: 40px;
    }

    .difference-item {
        padding: 25px 10px;
    }

    .journey-timeline {
        margin-top: 50px;
    }

    .timeline-item {
        grid-template-columns: 48px 20px minmax(0, 1fr);

        min-height: 170px;
    }

    .timeline-year {
        padding-right: 5px;

        font-size: 13px;
    }

    .timeline-item.reverse .timeline-year {
        padding-right: 5px;
        padding-left: 0;
    }

    .timeline-content,
    .timeline-item.reverse .timeline-content {
        padding-left: 12px;
    }

    .timeline-content h3 {
        font-size: 19px;
    }

    .timeline-content p {
        font-size: 12px;
    }

    .timeline-stats {
        flex-wrap: wrap;

        gap: 12px;
    }

    .timeline-stats strong {
        font-size: 17px;
    }

    .timeline-stats span {
        font-size: 12px;
    }

    .timeline-line {
        left: 58px;
    }

    .team-grid {
        gap: 45px;
    }

    .team-visual {
        width: min(100%, 330px);
    }

    .team-center {
        width: 85px;
        height: 85px;

        font-size: 28px;
    }

    .team-node {
        width: 45px;
        height: 45px;

        font-size: 14px;
    }

    .eco-node {
        width: 55px;
        height: 55px;
    }

    .eco-node i {
        font-size: 13px;
    }

    .eco-node span {
        font-size: 13px;
    }

    .eco-core {
        width: 100px;
        height: 100px;
    }

    .eco-core strong {
        font-size: 18px;
    }

    .eco-core span {
        font-size: 13px;
    }

    .final-cta {
        padding: 80px 0;
    }

    .final-cta-content h2 {
        font-size: 43px;
    }

    .final-cta-content p {
        font-size: 14px;
    }

}


/* ============================================================
   SMALL MOBILE
============================================================ */

@media (max-width: 420px) {

    .why-container {
        width: calc(100% - 22px);
    }

    .why-hero {
        padding-top: 100px;
        padding-bottom: 30px;
    }

    .hero-content h1 {
        font-size: 42px;
    }

    .hero-description {
        font-size: 13px;
    }

    .hero-visual {
        height: 290px;
        min-height: 290px;

        margin-top: 10px;
    }

    .orbit-one {
        width: 185px;
        height: 185px;
    }

    .orbit-two {
        width: 245px;
        height: 245px;
    }

    .orbit-three {
        width: 290px;
        height: 155px;
    }

    .core-circle {
        width: 108px;
        height: 108px;
    }

    .core-inner {
        width: 68px;
        height: 68px;
    }

    .core-inner span {
        font-size: 19px;
    }

    .floating-card {
        width: 104px;

        min-width: 104px;
        max-width: 104px;

        padding: 7px 8px;

        gap: 6px;
    }

    .floating-card .card-icon {
        width: 24px;
        height: 24px;

        flex-basis: 24px;

        font-size: 13px;
    }

    .floating-card strong {
        font-size: 13px;
    }

    .floating-card span {
        font-size: 13px;
    }

    .card-one {
        top: 25px;
        left: 0;
    }

    .card-two {
        top: 48px;
        right: 0;
    }

    .card-three {
        bottom: 38px;
        left: 0;
    }

    .card-four {
        bottom: 12px;
        right: 0;
    }

    .hero-meta-item strong {
        font-size: 21px;
    }

    .hero-meta-item span {
        font-size: 13px;
    }

    .difference-header h2,
    .team-content h2,
    .ecosystem-content h2,
    .section-heading h2 {
        font-size: 38px;
    }

    .final-cta-content h2 {
        font-size: 39px;
    }
}


/* ============================================================
   VERY SMALL PHONES
============================================================ */

@media (max-width: 360px) {

    .why-container {
        width: calc(100% - 18px);
    }

    .hero-content h1 {
        font-size: 37px;
    }

    .hero-description {
        font-size: 12px;
    }

    .hero-visual {
        height: 260px;
        min-height: 260px;
    }

    .orbit-one {
        width: 165px;
        height: 165px;
    }

    .orbit-two {
        width: 220px;
        height: 220px;
    }

    .orbit-three {
        width: 255px;
        height: 135px;
    }

    .core-circle {
        width: 95px;
        height: 95px;
    }

    .core-inner {
        width: 60px;
        height: 60px;
    }

    .core-inner span {
        font-size: 17px;
    }

    .floating-card {
        width: 94px;

        min-width: 94px;
        max-width: 94px;

        padding: 6px;
    }

    .floating-card .card-icon {
        width: 22px;
        height: 22px;

        flex-basis: 22px;
    }

    .floating-card strong {
        font-size: 6.5px;
    }

    .floating-card span {
        font-size: 5.5px;
    }

    .card-one {
        top: 20px;
    }

    .card-two {
        top: 40px;
    }

    .card-three {
        bottom: 32px;
    }

    .card-four {
        bottom: 8px;
    }

    .hero-meta {
        margin-top: 25px;
    }

    .hero-meta-item {
        padding: 8px 5px;
    }

    .hero-meta-item strong {
        font-size: 19px;
    }

    .hero-meta-item span {
        font-size: 6.5px;
    }
}


/* ============================================================
   TOUCH DEVICES
============================================================ */

@media (hover: none) {

    .hero-btn:hover,
    .approach-card:hover,
    .difference-item:hover,
    .process-card:hover,
    .cta-button:hover {
        transform: none;
    }
}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

    .floating-card {
        animation: none;
    }

    .hero-btn,
    .approach-card,
    .difference-item,
    .process-card,
    .cta-button {
        transition: none;
    }
}
</style>
